declare const window: any;

import { scrubPath, DataScrubber } from "./data-scrubber";
import { RouteUtils } from "./route-utils";
import { omit, last, mapValues, some } from "lodash";
import { IUser, IApp } from "@lib/common-interfaces";
import { getAppInsights, initAppInsights } from "./app-insights";

/**
 * CustomDimensions are used to attach string properties to AI objects.
 * You can add boolean and number properties here, but they will be
 * converted to strings before being sent, since AI will represent them
 * as strings anyway. If you need to track numeric properties, you should
 * use the CustomMetrics interface instead.
 */
export interface CustomDimensions {
  appId?: string;
  beacon?: string;
  id?: string;
  internalUser?: boolean;
  url?: string;
  urlTemplate?: string;
  path?: string;
  pathTemplate?: string;
  platform?: string;
  os?: string;
  method?: string;
  diagnosticContext?: string;
  status?: number;
  success?: boolean;
  commit?: string;
  wasCached?: boolean;
  referrer?: string;
}

/**
 * Used to attach numeric properties to AI objects.
 */

type OptionalMetricsDurations = {
  renderDuration?: number;
  totalDuration?: number;
};

export type CustomMetrics = OptionalMetricsDurations & { [key: string]: number };

export enum SeverityLevel {
  Warning = 2,
  Error = 3
}

export interface ITelemetryAppStore {
  app?: IApp;
  apps: IApp[];
}

export class Telemetry {
  private commit: string;
  private appStore?: ITelemetryAppStore;
  private path: string;
  private internalUser?: boolean;
  private userOrigin?: string;
  public beacon: string;
  private lastTrackPage?: { name: string; path?: string; props: CustomDimensions };
  private billingNotifiers?: { isPaid: boolean }[];
  /** Enable to see tracking calls logged to the console. */
  private loggingEnabled = false;

  constructor() {
    this.path = "";
    this.beacon = "";
    this.commit = (((window as any).initProps || {}).config || {}).commit || "unknown";
  }

  public init() {
    if (!getAppInsights()) {
      initAppInsights();

      if (this.lastTrackPage) {
        this.trackPage(this.lastTrackPage.name, this.lastTrackPage.path, this.lastTrackPage.props);
      }
    }
  }

  public setUser(user: IUser, account?: string) {
    const appInsights = getAppInsights();

    if (appInsights && user.id) {
      appInsights.setAuthenticatedUserContext(user.id, account);
    }

    if (user.email) {
      const domain = last(user.email.split("@"));
      if (domain) {
        this.internalUser = domain.toLowerCase() === "microsoft.com";
      }
    }

    this.userOrigin = user.origin;
  }

  public setAppStoreContext(appStore: ITelemetryAppStore) {
    this.appStore = appStore;
  }

  /**
   * Begins tracking whether the current app has been paid for.
   */
  public startTrackingPaidApps(billingNotifiers: { isPaid: boolean }[]) {
    this.billingNotifiers = billingNotifiers;
  }

  public setPathContext(path: string) {
    this.path = path;
    this.beacon = this.getBeacon(path);
  }

  public trackPage(
    name: string,
    path?: string,
    customDimensions: CustomDimensions = {},
    customMetrics: CustomMetrics = {},
    duration?: number
  ) {
    const aiProps = Object.assign({}, this.commonProps, customDimensions);
    if (path) {
      aiProps.beacon = this.getBeacon(path);
      aiProps.path = scrubPath(path);
    }

    const appInsights = getAppInsights();

    if (appInsights) {
      delete aiProps.path;

      const githubRefferer = DataScrubber.scrubGitHubUrl(document.referrer);
      if (githubRefferer) {
        aiProps.referrer = githubRefferer;
      }

      appInsights.trackPageView({
        name,
        uri: path || this.path,
        properties: mapValues(aiProps, String),
        measurements: customMetrics
      });
      appInsights.trackPageViewPerformance({
        name,
        uri: path || this.path,
        properties: mapValues(aiProps, String),
        measurements: customMetrics,
        duration: duration + ""
      });
    } else {
      // since cookie consent may not execute before the first trackPage call, we save the last value.
      this.lastTrackPage = {
        name: name,
        path: path,
        props: aiProps
      };
    }
  }

  public trackEvent(eventName: string, props?: any, measurements?: any) {
    const appInsights = getAppInsights();

    if (appInsights) {
      const aiProps = Object.assign({}, this.commonProps, props);
      appInsights.trackEvent({
        name: eventName,
        properties: aiProps,
        measurements
      });

      if (process.env.NODE_ENV !== "production" && this.loggingEnabled) {
        console.log(`Track Event: ${eventName}`, aiProps);
      }
    }
  }

  // Use SeverityLevel.Warning (default) for handled business errors. Use SeverityLevel.Error
  // for handled code errors that require immediate attention.
  public trackException(exception: any, props?: any, severityLevel = SeverityLevel.Warning) {
    const aiProps = Object.assign({}, this.commonProps, props);
    const appInsights = getAppInsights();

    if (appInsights) {
      appInsights.trackException({
        exception,
        properties: aiProps,
        severityLevel
      });

      if (process.env.NODE_ENV !== "production" && this.loggingEnabled) {
        console.log(`Track Exception: ${exception}`, aiProps);
      }
    }
  }

  public trackAjax(id: string, totalTime: number, props: CustomDimensions) {
    const appInsights = getAppInsights();

    if (appInsights) {
      const aiProps = Object.assign({}, this.commonProps, props);
      const remainingProps: any = omit(aiProps, ["id", "method", "url", "path", "success", "status"]);
      // This function allegedly does not take arbitrary extra properties (`remainingProps`), but looking
      // in Analytics it sort of looks like it might be working, so I’m not going to touch it for now.
      // https://analytics.applicationinsights.io/subscriptions/f4147d36-1601-4338-ae2c-693eb61b75d8/resourcegroups/AppHub_INT/components/apphub%20int#/discover/query/results/table?apptype=other&resourceId=%252Fsubscriptions%252Ff4147d36-1601-4338-ae2c-693eb61b75d8%252Fresourcegroups%252FAppHub_INT%252Fproviders%252Fmicrosoft.insights%252Fcomponents%252Fapphub%2520int
      appInsights.trackDependencyData({
        id,
        name: aiProps.method + " " + aiProps.path,
        success: aiProps.success,
        responseCode: aiProps.status || 0,
        properties: remainingProps
      });
    }
  }

  /**
   * Flushes the event queue and tracks all events immediately.
   */
  public flush(): void {
    const appInsights = getAppInsights();

    if (appInsights) {
      appInsights.flush();
    }
  }

  private get commonProps() {
    const app = this.appStore && this.appStore.app;
    const props = {
      beacon: this.beacon,
      path: scrubPath(this.path),
      appId: app && app.id,
      internalUser: this.internalUser,
      commit: this.commit,
      appOrigin: app && app.origin,
      isPaid: this.isPaid(),
      userOrigin: this.userOrigin
    };

    const os = this.getOS();
    if (os) {
      props["os"] = os;
    }

    const platform = this.getPlatform();
    if (platform) {
      props["platform"] = platform;
    }

    if (app && app.microsoft_internal) {
      // track whether or not the current app is enabled for MS-internal dogfooding
      props["microsoftInternalApp"] = true;
    }

    return props;
  }

  public getBeacon(path: string): string {
    return RouteUtils.extractBeaconFromPath(window.location.host, path);
  }

  private isPaid(): boolean | undefined {
    if (this.billingNotifiers) {
      // If there's paid for just _one_ plan, we're happy
      if (some(this.billingNotifiers, (notifier) => notifier.isPaid)) {
        return true;
      }
      // If there are still plans we don't know about, the result is undefined
      if (some(this.billingNotifiers, (notifier) => notifier.isPaid === undefined)) {
        return undefined;
      }

      return false;
    }

    return undefined;
  }

  private getOS(): string | undefined {
    if (this.appStore && this.appStore.app) {
      return this.appStore.app.os;
    }
  }

  private getPlatform(): string | undefined {
    if (this.appStore && this.appStore.app) {
      return this.appStore.app.platform;
    }
  }
}

export const telemetry = new Telemetry();
